import { ReactNode } from "react";
import { Instagram, Mail, Phone } from "lucide-react";
import type { LandingSettings } from "@/hooks/useLandingPage";
import logo from "@/assets/logo-small.webp";
import { useMediaProtection } from "@/hooks/useMediaProtection";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Minimal standalone shell for the landing page.
 * Deliberately has no navigation to other pages — the landing page is a
 * single, self-contained page.
 */
export function LandingShell({
  settings,
  children,
}: {
  settings: LandingSettings;
  children: ReactNode;
}) {
  const { isStaff } = useAuth();
  useMediaProtection(!isStaff());

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {settings.header_enabled !== false && (
        <header className="absolute top-0 inset-x-0 z-30">
          <div className="container mx-auto px-6 h-20 flex items-center">
            {settings.logo_url ? (
              <img
                src={settings.logo_url}
                alt={settings.brand_name || "Logo"}
                className="h-12 w-auto object-contain"
              />
            ) : (
              <img src={logo} alt={settings.brand_name || "Logo"} className="h-12 w-auto object-contain" />
            )}
            {settings.brand_name && (
              <span className="ml-4 text-sm tracking-[0.3em] uppercase text-muted-foreground">
                {settings.brand_name}
              </span>
            )}
          </div>
        </header>
      )}

      <main className="flex-1">{children}</main>

      {settings.footer_enabled !== false && (
        <footer className="border-t border-border py-10">
          <div className="container mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6 text-sm text-muted-foreground">
            <p>
              {settings.footer_text ||
                `© ${new Date().getFullYear()} ${settings.brand_name || "Himilo Studio"}`}
            </p>
            <div className="flex flex-wrap items-center gap-6">
              {settings.contact_email && (
                <a href={`mailto:${settings.contact_email}`} className="flex items-center gap-2 hover:text-primary">
                  <Mail className="h-4 w-4" />
                  {settings.contact_email}
                </a>
              )}
              {settings.contact_phone && (
                <a href={`tel:${settings.contact_phone}`} className="flex items-center gap-2 hover:text-primary">
                  <Phone className="h-4 w-4" />
                  {settings.contact_phone}
                </a>
              )}
              {settings.contact_instagram && (
                <a
                  href={settings.contact_instagram}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 hover:text-primary"
                >
                  <Instagram className="h-4 w-4" />
                  Instagram
                </a>
              )}
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}

export default LandingShell;
